/**
 * Shared hook powering the public proposal's attachment surfaces:
 *   - The slim "from your Medicys team" banner at the very top
 *     (`AttachmentBanner.jsx`).
 *   - The prominent download card inside the Costs tab
 *     (`CostsAttachmentsCard.jsx`).
 *
 * Both surfaces hit the same backend endpoint, so we lift the fetch +
 * download flow up here. The page-level call in `PublicProposal.jsx`
 * then forwards `attachments` and `downloadAttachment` to whichever
 * surface(s) need them, no double network requests, no duplicate
 * spinner logic, no drift between the two views.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function usePublicAttachments(token, onDownloadEvent, ready = true) {
  // `null` = haven't fetched yet → callers render nothing (avoids the
  // flash of an empty banner on first paint). `[]` = fetched, none.
  const [items, setItems] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    // Wait for the page-level gate check to clear before hitting the
    // attachments endpoint. Calling /attachments before the gate
    // cookie is set returns 403, which would cache `items=[]` and
    // silently hide both the top banner and the in-Costs card even
    // though attachments do exist - exactly the bug staff reported.
    if (!token || !ready) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/api/public/proposals/${encodeURIComponent(token)}/attachments`, {
        withCredentials: true,
      })
      .then((r) => { if (!cancelled) setItems(r.data?.attachments || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [token, ready]);

  const downloadAttachment = useCallback(async (att) => {
    if (!token || !att) return;
    setDownloadingId(att.id);
    if (typeof onDownloadEvent === "function") {
      onDownloadEvent({
        attachment_id: att.id,
        filename: att.filename,
        format: att.ext,
        // "client_grid" distinguishes the staff-uploaded cost-grid
        // (the file the CLIENT provided and asked us to fill in) from
        // our auto-generated /proposal.xlsx export. The Engagement
        // panel renders each kind with a different label so deal
        // owners can tell which spreadsheet the client is engaging
        // with - "their grid" vs "our grid".
        kind: "client_grid",
      });
    }
    try {
      // Fetch with credentials so the gate cookie travels along.
      const resp = await fetch(
        `${API}/api/public/proposals/${encodeURIComponent(token)}/attachments/${encodeURIComponent(att.id)}/download`,
        { credentials: "include" },
      );
      if (!resp.ok) throw new Error(`Server ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Swallow - caller's UI just re-enables the button; the client
      // can retry by clicking again.
    } finally {
      setDownloadingId(null);
    }
  }, [token, onDownloadEvent]);

  return { items, downloadingId, downloadAttachment };
}
